import { useState, useMemo, useEffect } from "react";
import {
  Text,
  TwButton as Button,
  TwSelector as Selector,
  Icon,
  Input,
  Chip,
  Accordion
} from "@ui-component-library";
import { DynamicModal } from "@components/DynamicModal";
import { AlertDialog } from "@components/PropertySelector/components/AlertModal";
import { searchProps, getPropertyData } from "@services/property";
import { SkeletonLoader } from "@components/PropertySelector/components/SkeletonLoaders";
import { usePropsDataStore } from "@store/PropsDataStore";

const svSort = (a, b) =>
  a?.designation?.localeCompare(b?.designation ?? "", "sv", { sensitivity: "base" });

export default function SelectPropertyDialog({
  isOpen,
  onClose,
  userProperties,
  selectedProperties,
  onSelect,
  onConfirm,
  startedAsGuest,
  allowNonPropOwnedSelection = false,
  allowPlusOwnedSelection = false,
  plusOnlyOwnedSelection = false,
  hideSearchField = false,
  maxPendingSelections = undefined,
}) {
  // New subscription checkout does not expose setAlertDialog; use local alert state only.
  const [standaloneAlertDialog, setStandaloneAlertDialog] = useState(null);
  const setAlertDialog = setStandaloneAlertDialog;

  const { props } = usePropsDataStore();

  const [pendingSelected, setPendingSelected] = useState(new Map());
  const [propertyResults, setPropertyResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && hideSearchField) {
      setPropertyResults(null);
    }
  }, [isOpen, hideSearchField]);

  useEffect(() => {
    if (!isOpen || !selectedProperties) return;
    setPendingSelected(
      new Map(selectedProperties.map((property) => {
        const propOption = propOptions.find((p) => p.ref === property.ref);
        return [property.ref, {
          designation: property.designation?.toLowerCase(),
          isOwned: true,
          isPlus: propOption?.dfr ?? false,
        }];
      }))
    );
  }, [isOpen]);

  const propOptions = useMemo(() => {
    const options = [...(Window.react_data?.owned_props ?? [])];
    props.forEach((storeProp) => {
      const alreadyListed = options.some(
        (existingProp) => existingProp.ref == storeProp.ref
      );
      if (!alreadyListed) {
        options.push(storeProp);
      }
    });
    return options;
  }, [props]);

  const isOptionDisabled = (option) => {
    // Shared (non-owned) properties can never be used for owner-only actions.
    if (option?.isShared) {
      return true;
    }
    if (allowNonPropOwnedSelection) {
      return ["broken", "running"].includes(option?.status);
    }
    const blockPlus = option.dfr && !allowPlusOwnedSelection;
    const blockWithoutPlus = plusOnlyOwnedSelection && !option.dfr;
    return (
      option.type !== "PROP" ||
      blockPlus ||
      blockWithoutPlus ||
      option.status === "running"
    );
  };
  const isLoadingProperty = (option) => option.status === "running";

  const mapOwnedProp = (property) => ({
    ...property,
    key: property.ref,
    designation: property.designation?.toLowerCase(),
    value: property.ref,
    isOwned: true,
    isPlus: property.dfr,
    isShared: property.role ? property.role !== "owner" : Boolean(property.is_shared),
  });

  const handleSearch = async (searchText) => {
    if (hideSearchField) {
      return;
    }
    if (searchText === "") {
      setPropertyResults(null);
      return;
    }

    setIsLoading(true);
    const result = await searchProps(searchText);

    const searchQuery = searchText.toLowerCase();
    const ownedRefs = new Set(propOptions.map((property) => property.ref));

    const ownedResults = propOptions
      .filter((property) => property.designation?.toLowerCase().includes(searchQuery))
      .map(mapOwnedProp);

    const searchResults = result.data
      .filter((item) => !ownedRefs.has(item.registerenhet))
      .map((item) => ({
        key: item.objektidentitet,
        designation: item.beteckning.toLowerCase(),
        value: item.registerenhet,
        isOwned: false,
      }));

    setPropertyResults([...ownedResults, ...searchResults].sort(svSort));
    setIsLoading(false);
  };

  const handleSelect = (item) => {
    const { value, designation, isOwned, isPlus } = item;
    setPendingSelected((prev) => {
      const next = new Map(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        if (maxPendingSelections === 1) {
          next.clear();
        }
        next.set(value, { designation, isOwned, isPlus });
      }
      const sortedEntries = Array.from(next.entries()).sort(([, a], [, b]) =>
        (a.designation || "").localeCompare(b.designation || "", "sv", { sensitivity: "base" })
      );
      return new Map(sortedEntries);
    });
  };

  const handleConfirm = async () => {
    if (!selectedProperties) return;

    const originalRefs = new Set(selectedProperties.map((p) => p.ref));

    const syncOwned = () => {
      if (maxPendingSelections === 1) {
        for (const { ref, designation } of selectedProperties) {
          if (!pendingSelected.has(ref)) {
            onSelect({ label: designation, value: ref });
          }
        }
        for (const [value, { isOwned }] of pendingSelected) {
          if (isOwned && !originalRefs.has(value)) {
            onSelect({
              label: pendingSelected.get(value).designation,
              value,
            });
          }
        }
        return;
      }
      for (const [value, { isOwned }] of pendingSelected) {
        if (isOwned && !originalRefs.has(value)) {
          onSelect({
            label: pendingSelected.get(value).designation,
            value,
          });
        }
      }
      for (const { ref, designation } of selectedProperties) {
        if (!pendingSelected.has(ref)) {
          onSelect({ label: designation, value: ref });
        }
      }
    };

    syncOwned();

    const searchedRefs = Array.from(pendingSelected.entries())
      .filter(([_, { isOwned }]) => !isOwned)
      .map(([value]) => value);

    if (searchedRefs.length > 0 && !hideSearchField) {
      try {
        setIsLoading(true);
        const result = await getPropertyData(searchedRefs);

        const { properties } = result.data;
        const getDesignation = (lmID) => pendingSelected.get(lmID)?.designation ?? "";

        const toSmallProperties = properties.filter((property) => property.to_small);
        const bigProperties = properties.filter((property) => property.prop_big_area);
        const otherProperties = properties.filter((property) => !property.to_small && !property.prop_big_area);

        if (toSmallProperties.length > 0) {
          setAlertDialog({
            title: "En eller flera fastigheter är för små",
            subtitle:
              "Följande fastiget(er) kommer inte att kunna läggas till då de är mindre än den minsta storlek som krävs för att köpa plus",
            designation: toSmallProperties.map((p) => getDesignation(p.lmID)).join(", "),
            helperInfo: "Vänligen välj en annan fastighet - eller kontakta oss för rådgivning.",
          });
        }

        if (bigProperties.length > 0) {
          setAlertDialog({
            title: "En eller flera fastigheter är för stor",
            subtitle:
              "Valda fastigheten är större än den största storlek som krävs för att köpa plus",
            designation: bigProperties.map((p) => getDesignation(p.lmID)).join(", "),
            helperInfo: "Vänligen välj en annan fastighet - eller kontakta oss för rådgivning.",
          });
        }

        if (result.ok && otherProperties.length > 0 && onConfirm) {
          onConfirm({
            properties: otherProperties.map((property) => ({
              ...property,
              designation: getDesignation(property.lmID),
            })),
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }

    handleClose();
  };

  const handleClose = () => {
    onClose();
    setPropertyResults(null);
    setPendingSelected(new Map());
  };

  const handleRemove = (value) => {
    setPendingSelected((prev) => {
      const next = new Map(prev);
      next.delete(value);
      return next;
    });
  };

  const defaultList = useMemo(() => {
    if (startedAsGuest) {
      return []
    };
    
    return propOptions.map(mapOwnedProp).sort(svSort);
  }, [startedAsGuest, propOptions]);

  const list = propertyResults ?? defaultList;
  const hasSearched = propertyResults !== null;
  const originalRefs = new Set((selectedProperties ?? []).map((p) => p.ref));
  const hasPendingAdditions = Array.from(pendingSelected.keys()).some((ref) => !originalRefs.has(ref));
  const hasPendingRemovals = (selectedProperties ?? []).some((p) => !pendingSelected.has(p.ref));
  const hasPendingChanges = hasPendingAdditions || hasPendingRemovals;

  return (
    <>
    <DynamicModal
      bp={{ sm: "sheet", lg: "sheet" }}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <DynamicModal.Header>
        <div className="flex w-full justify-between items-center">
          <Text variant="text-lg/leading-normal/medium">
            {hideSearchField
              ? "Välj fastighet"
              : startedAsGuest
                ? "Sök efter fastighet(er)"
                : "Välj fastighet"}
          </Text>
          <Button variant="tertiary" size="icon" onClick={handleClose}>
            <Icon
              name="CrossSimple"
              className="color-charcoal-500"
              size="large"
            />
          </Button>
        </div>
      </DynamicModal.Header>
      <DynamicModal.Content className="relative p-0">
        {!hideSearchField && (
          <div className="bg-white-50 sticky top-0 left-0 z-30 flex w-full flex-col gap-3 border-b border-solid border-[#E6E6E6] p-4">
            <Input
              variant="fluid"
              name="search"
              label="Ange en fastighetsbeteckning"
              placeholder="Sök t.ex. Ydre Sevedstorp 1:8"
              onChange={handleSearch}
              prefix={<Icon name="MagnifyingGlassSimple" size="medium" />}
            />
            {!startedAsGuest && !hasSearched && (
              <Text
                variant="text-sm/leading-normal/normal"
                className="text-[#737373] mt-2"
              >
                Visar fastigheter från Lantmäteriets register
              </Text>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2 p-4">
          {isLoading && (
            <div className="mt-6 flex flex-col gap-3">
              <SkeletonLoader length={10} type="searchResult" />
            </div>
          )}

          {!isLoading && list.length > 0 && (
            <Selector>
              {list.map((item) => (
                <div
                  key={item.key}
                  className="flex flex-row gap-1 justify-between items-center relative"
                >
                  <Selector.Option
                    label={item.designation}
                    value={item.value}
                    showDefaultIcon
                    isSelected={pendingSelected.has(item.value)}
                    onClick={() => handleSelect(item)}
                    disabled={item.isOwned && isOptionDisabled(item)}
                    className="capitalize gap-1"
                  >
                    <div className="flex flex-row items-center gap-1">
                      {item.isShared ? (
                        <Chip
                          label="Delad – endast ägaren"
                          className="bg-[#F4F5F5] text-[#30353A]"
                        />
                      ) : item.isOwned ? (
                        <>
                          <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                            Tillagd
                          </Text>
                          <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
                          {item.isPlus ? (
                            <Chip
                              label="Plus"
                              className="bg-[#F6F5FE] text-[#6856C3] fill-[#6856C3]"
                              icon="SparkleFilled"
                            />
                          ) : (
                            <Chip
                              label="Saknar Plus"
                              className="bg-[#F4F5F5] text-[#30353A]"
                            />
                          )}
                        </>
                      ) : null}
                    </div>
                  </Selector.Option>
                  {isLoadingProperty(item) && (
                    <div className="bg-neutral-cloud flex flex-row gap-2 items-center rounded-md px-4 py-2 cursor-not-allowed">
                      <div
                        className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-charcoal-200 border-t-transparent"
                        role="status"
                      />
                      <Text
                        variant="text-sm/leading-normal/medium"
                        className="text-charcoal-200"
                      >
                        Laddar...
                      </Text>
                    </div>
                  )}
                </div>
              ))}
            </Selector>
          )}

          {!isLoading && hasSearched && list.length === 0 && (
            <div className="p-4">
              <Text
                variant="text-sm/leading-normal/medium"
                className="text-[#737373]"
              >
                Inga resultat hittades.
              </Text>
            </div>
          )}

          {!isLoading &&
            hideSearchField &&
            !list.length &&
            !hasSearched && (
              <div className="p-4">
                <Text
                  variant="text-sm/leading-normal/medium"
                  className="text-[#737373]"
                >
                  Du har inga fastigheter i Min skog ännu. Stäng dialogen och
                  lägg till en fastighet först.
                </Text>
              </div>
            )}
        </div>
      </DynamicModal.Content>
      <DynamicModal.Footer className="p-0 gap-2">
        { pendingSelected.size > 0 && (
          <Accordion className="bg-[#F6F5F4] rounded-t-lg py-3 px-4">
            <Accordion.Header className="flex flex-row gap-4  justify-between">
              <div className="flex flex-row gap-1 items-center w-full justify-between">
                <div className="flex flex-row gap-2 items-center justify-start">
                  <Text variant="text-xs/leading-normal/semibold" className="bg-[#0F0F0F] h-5 w-5 rounded-full flex items-center justify-center text-white-50">
                    <span>{pendingSelected.size}</span>
                  </Text>
                  <Text variant="text-base/leading-normal/medium">Valda fastigheter</Text>
                </div>
                <Button variant="tertiary" size="small" onClick={() => setPendingSelected(new Map())}>
                  Rensa
                </Button>
              </div>
            </Accordion.Header>
            <Accordion.Content className="flex flex-col ">
              {Array.from(pendingSelected.entries()).map(([value, item]) => (
                <div key={value} className="flex flex-row gap-1 items-center justify-between py-1-5 pl-4 pr-1 rounded-lg bg-white-50 mt-2">
                  <div className="flex flex-col gap-1">
                    <Text variant="text-sm/leading-normal/medium" className="capitalize">{item.designation}</Text>
                    <div className="flex flex-row items-center gap-1">
                      {item.isOwned && (
                        <div className="flex flex-row items-center gap-1">
                          <Text variant="text-xs/leading-normal/normal" className="text-[#737373]">
                            Tillagd
                          </Text>
                          <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
                          {item.isPlus ? (
                            <Chip
                              label="Plus"
                              className="bg-[#F6F5FE] text-[#6856C3] fill-[#6856C3]"
                              icon="SparkleFilled"
                            />
                          ) : (
                            <Chip
                              label="Saknar Plus"
                              className="bg-[#F4F5F5] text-[#30353A]"
                            />
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                  <Button variant="tertiary" size="icon" onClick={() => handleRemove(value)}>
                    <Icon name="CrossSimple" size="large" />
                  </Button>
                </div>
              ))}
            </Accordion.Content>
          </Accordion>
        )}
        <div className="p-4">
          <Button
            variant="primary"
            fullWidth
            disabled={isLoading || pendingSelected.size === 0}
            onClick={hasPendingChanges ? handleConfirm : handleClose}
            >
             {isLoading ? "Laddar..." : "Lägg till"}
            {pendingSelected.size > 0 && !isLoading && (
              <Text variant="text-xs/leading-normal/semibold" className="bg-[#F5F5F5] h-5 w-5 rounded-full flex items-center justify-center text-[#1A1A1A]">
                <span>{pendingSelected.size}</span>
              </Text>
            )}
          </Button>
        </div>
      </DynamicModal.Footer>
    </DynamicModal>
    {standaloneAlertDialog ? (
      <AlertDialog
        {...standaloneAlertDialog}
        isOpen={!!standaloneAlertDialog}
        onClose={() => setStandaloneAlertDialog(null)}
      />
    ) : null}
    </>
  );
}
